import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getApiBaseUrl } from '../../config/api';
import { getAuthorDisplayName } from '../../utils/displayName';
import { resolveAvatarUrl, handleAvatarError } from '../../utils/profilePicUrl';
import { buildMentionToken, registerMentionPeople } from '../../utils/mentions';
import './MentionInput.css';

const API_URL = getApiBaseUrl();

function connectionLabel(person) {
  return getAuthorDisplayName(person);
}

function connectionSubtitle(person) {
  const username = String(person?.username || '').trim();
  if (username && !username.includes('@')) return `@${username}`;
  return person?.country || '';
}

/**
 * Textarea with @-mention autocomplete for accepted connections.
 */
export default function MentionInput({
  value,
  onChange,
  userId,
  placeholder = 'Write something… Type @ to mention a connection',
  rows = 4,
  disabled = false,
  style = {},
  className,
  id,
  required = false,
  onKeyDown,
  showTools = false,
}) {
  const textareaRef = useRef(null);
  const [connections, setConnections] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [mentionQuery, setMentionQuery] = useState(null); // null = closed
  const [mentionStart, setMentionStart] = useState(-1);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!userId || loaded) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/get_connections.php?user_id=${userId}`);
        const data = await res.json();
        if (!cancelled && data.success) {
          const list = data.connections || [];
          setConnections(list);
          registerMentionPeople(list);
        }
      } catch (err) {
        console.error('Failed to load connections for mentions:', err);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, loaded]);

  const suggestions = useMemo(() => {
    if (mentionQuery === null) return [];
    const q = mentionQuery.trim().toLowerCase();
    const list = connections || [];
    if (!q) return list.slice(0, 8);
    return list
      .filter((p) => {
        const name = connectionLabel(p).toLowerCase();
        const username = String(p.username || '').toLowerCase();
        return name.includes(q) || username.includes(q);
      })
      .slice(0, 8);
  }, [connections, mentionQuery]);

  const closeMentions = () => {
    setMentionQuery(null);
    setMentionStart(-1);
    setActiveIndex(0);
  };

  const detectMention = (text, caret) => {
    const before = text.slice(0, caret);
    const at = before.lastIndexOf('@');
    if (at < 0) return null;
    if (at > 0 && /[A-Za-z0-9_]/.test(before[at - 1])) return null;
    const fragment = before.slice(at + 1);
    if (fragment.includes('\n') || fragment.includes(']') || /\s/.test(fragment)) {
      return null;
    }
    // Don't reopen inside an already-inserted token
    if (fragment.includes('(user:')) return null;
    if (fragment.length > 40) return null;
    return { start: at, query: fragment };
  };

  const handleChange = (e) => {
    const next = e.target.value;
    const caret = e.target.selectionStart ?? next.length;
    onChange?.(next);

    const hit = detectMention(next, caret);
    if (hit) {
      setMentionStart(hit.start);
      setMentionQuery(hit.query);
      setActiveIndex(0);
    } else {
      closeMentions();
    }
  };

  const insertMention = (person) => {
    const token = buildMentionToken(person);
    if (!token) return;
    const el = textareaRef.current;
    const text = value || '';
    const caret = el?.selectionStart ?? text.length;
    const start = mentionStart >= 0 ? mentionStart : caret;
    const before = text.slice(0, start);
    const after = text.slice(caret);
    const next = `${before}${token} ${after}`;
    onChange?.(next);
    closeMentions();

    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const pos = before.length + token.length + 1;
      el.setSelectionRange(pos, pos);
    });
  };

  const handleKeyDown = (e) => {
    if (mentionQuery !== null && suggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % suggestions.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insertMention(suggestions[activeIndex]);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        closeMentions();
        return;
      }
    }
    onKeyDown?.(e);
  };

  const insertAtCaret = (snippet, openMention = false) => {
    const el = textareaRef.current;
    const text = value || '';
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? start;
    const next = `${text.slice(0, start)}${snippet}${text.slice(end)}`;
    onChange?.(next);
    requestAnimationFrame(() => {
      if (!el) return;
      el.focus();
      const pos = start + snippet.length;
      el.setSelectionRange(pos, pos);
      if (openMention) {
        const hit = detectMention(next, pos);
        if (hit) {
          setMentionStart(hit.start);
          setMentionQuery(hit.query);
          setActiveIndex(0);
        }
      }
    });
  };

  const showDropdown = mentionQuery !== null && userId;

  return (
    <div
      style={{ position: 'relative', width: '100%' }}
      className={`mention-input-root${showTools ? ' mention-input-root--tools' : ''}${className ? ` ${className}` : ''}`}
    >
      <textarea
        id={id}
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        required={required}
        className={showTools ? 'mention-input-field' : undefined}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          ...style,
          ...(showTools
            ? { paddingBottom: 36, paddingRight: 72, resize: 'vertical' }
            : {}),
        }}
      />

      {showTools && (
        <div className="mention-input-tools" aria-hidden={false}>
          <button
            type="button"
            className="mention-input-tool"
            title="Mention someone"
            disabled={disabled}
            onClick={() => insertAtCaret('@', true)}
          >
            @
          </button>
          <button
            type="button"
            className="mention-input-tool"
            title="Add emoji"
            disabled={disabled}
            onClick={() => insertAtCaret('😊')}
          >
            🙂
          </button>
        </div>
      )}

      {showDropdown && (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: '100%',
            marginTop: 4,
            zIndex: 50,
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 10,
            boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
            maxHeight: 240,
            overflowY: 'auto',
          }}
        >
          {!loaded ? (
            <div style={{ padding: '10px 12px', fontSize: 13, color: '#64748b' }}>
              Loading connections…
            </div>
          ) : suggestions.length === 0 ? (
            <div style={{ padding: '10px 12px', fontSize: 13, color: '#64748b' }}>
              {connections.length === 0
                ? 'No connections to mention yet.'
                : 'No matching connections.'}
            </div>
          ) : (
            suggestions.map((person, idx) => {
              const active = idx === activeIndex;
              return (
                <button
                  key={person.id}
                  type="button"
                  role="option"
                  aria-selected={active}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    insertMention(person);
                  }}
                  onMouseEnter={() => setActiveIndex(idx)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    width: '100%',
                    border: 'none',
                    background: active ? '#eff6ff' : 'transparent',
                    padding: '8px 12px',
                    cursor: 'pointer',
                    textAlign: 'left',
                  }}
                >
                  <img
                    src={resolveAvatarUrl(person, API_URL)}
                    alt=""
                    onError={handleAvatarError}
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      objectFit: 'cover',
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ minWidth: 0 }}>
                    <span
                      style={{
                        display: 'block',
                        fontSize: 14,
                        fontWeight: 600,
                        color: '#0f172a',
                      }}
                    >
                      {connectionLabel(person)}
                    </span>
                    {connectionSubtitle(person) ? (
                      <span
                        style={{
                          display: 'block',
                          fontSize: 12,
                          color: '#64748b',
                        }}
                      >
                        {connectionSubtitle(person)}
                      </span>
                    ) : null}
                  </span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
